import { enGB } from "date-fns/locale";
import buildLocalizeFn from "date-fns/locale/_lib/buildLocalizeFn";
import buildFormatLongFn from "date-fns/locale/_lib/buildFormatLongFn";

/**
 * Swahili for date-fns, which ships no Swahili locale in v2. Built on en-GB (Monday-first weeks,
 * day-first dates) with Swahili month and day names, distances and relative days.
 *
 * Times are written on the 24-hour clock ("14:30"), not with AM/PM or the Swahili clock that
 * counts from sunrise ("saa nane mchana"), so a time can never be read six hours off.
 *
 * Parsing (`match`) is still en-GB's: nothing in the app parses spelled-out Swahili dates.
 */

const localize = {
  ...enGB.localize,
  ordinalNumber: (dirtyNumber) => String(Number(dirtyNumber)),
  era: buildLocalizeFn({
    values: {
      narrow: ["KK", "BK"],
      abbreviated: ["KK", "BK"],
      wide: ["Kabla ya Kristo", "Baada ya Kristo"],
    },
    defaultWidth: "wide",
  }),
  quarter: buildLocalizeFn({
    values: {
      narrow: ["1", "2", "3", "4"],
      abbreviated: ["R1", "R2", "R3", "R4"],
      wide: ["Robo ya 1", "Robo ya 2", "Robo ya 3", "Robo ya 4"],
    },
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1,
  }),
  month: buildLocalizeFn({
    values: {
      narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
      abbreviated: ["Jan", "Feb", "Mac", "Apr", "Mei", "Jun", "Jul", "Ago", "Sep", "Okt", "Nov", "Des"],
      wide: [
        "Januari",
        "Februari",
        "Machi",
        "Aprili",
        "Mei",
        "Juni",
        "Julai",
        "Agosti",
        "Septemba",
        "Oktoba",
        "Novemba",
        "Desemba",
      ],
    },
    defaultWidth: "wide",
  }),
  day: buildLocalizeFn({
    values: {
      // Sunday first, as date-fns expects. Most Swahili day names start with "Ju", so the
      // shorter forms keep three letters to stay distinguishable in calendar headers.
      narrow: ["Jpi", "Jtt", "Jnn", "Jtn", "Alh", "Iju", "Jmo"],
      short: ["Jpi", "Jtt", "Jnn", "Jtn", "Alh", "Iju", "Jmo"],
      abbreviated: ["Jpi", "Jtt", "Jnn", "Jtn", "Alh", "Iju", "Jmo"],
      wide: ["Jumapili", "Jumatatu", "Jumanne", "Jumatano", "Alhamisi", "Ijumaa", "Jumamosi"],
    },
    defaultWidth: "wide",
  }),
};

const formatLong = {
  date: buildFormatLongFn({
    formats: { full: "EEEE, d MMMM yyyy", long: "d MMMM yyyy", medium: "d MMM yyyy", short: "dd/MM/yyyy" },
    defaultWidth: "full",
  }),
  time: buildFormatLongFn({
    formats: { full: "HH:mm:ss zzzz", long: "HH:mm:ss z", medium: "HH:mm:ss", short: "HH:mm" },
    defaultWidth: "full",
  }),
  dateTime: buildFormatLongFn({
    formats: { full: "{{date}}, {{time}}", long: "{{date}}, {{time}}", medium: "{{date}}, {{time}}", short: "{{date}}, {{time}}" },
    defaultWidth: "full",
  }),
};

// Swahili puts the number after the noun ("dakika 5"), and "ago" agrees with the noun's class:
// N-class nouns (sekunde, dakika, saa, siku, wiki) take iliyopita/zilizopita, mwezi/miezi and
// mwaka/miaka take uliopita/iliyopita.
const N_CLASS = { pastOne: "iliyopita", pastOther: "zilizopita" };
const M_MI_CLASS = { pastOne: "uliopita", pastOther: "iliyopita" };

const DISTANCES = {
  lessThanXSeconds: { one: "chini ya sekunde moja", other: "chini ya sekunde {{count}}", ...N_CLASS },
  xSeconds: { one: "sekunde 1", other: "sekunde {{count}}", ...N_CLASS },
  halfAMinute: { one: "nusu dakika", other: "nusu dakika", pastOne: "iliyopita", pastOther: "iliyopita" },
  lessThanXMinutes: { one: "chini ya dakika moja", other: "chini ya dakika {{count}}", ...N_CLASS },
  xMinutes: { one: "dakika 1", other: "dakika {{count}}", ...N_CLASS },
  aboutXHours: { one: "takriban saa 1", other: "takriban saa {{count}}", ...N_CLASS },
  xHours: { one: "saa 1", other: "saa {{count}}", ...N_CLASS },
  xDays: { one: "siku 1", other: "siku {{count}}", ...N_CLASS },
  aboutXWeeks: { one: "takriban wiki 1", other: "takriban wiki {{count}}", ...N_CLASS },
  xWeeks: { one: "wiki 1", other: "wiki {{count}}", ...N_CLASS },
  aboutXMonths: { one: "takriban mwezi 1", other: "takriban miezi {{count}}", ...M_MI_CLASS },
  xMonths: { one: "mwezi 1", other: "miezi {{count}}", ...M_MI_CLASS },
  aboutXYears: { one: "takriban mwaka 1", other: "takriban miaka {{count}}", ...M_MI_CLASS },
  xYears: { one: "mwaka 1", other: "miaka {{count}}", ...M_MI_CLASS },
  overXYears: { one: "zaidi ya mwaka 1", other: "zaidi ya miaka {{count}}", ...M_MI_CLASS },
  almostXYears: { one: "karibu mwaka 1", other: "karibu miaka {{count}}", ...M_MI_CLASS },
};

const formatDistance = (token, count, options) => {
  const entry = DISTANCES[token];
  const one = count === 1;
  const text = (one ? entry.one : entry.other).replace("{{count}}", String(count));
  if (!options?.addSuffix) return text;
  if (options.comparison && options.comparison > 0) return `baada ya ${text}`;
  return `${text} ${one ? entry.pastOne : entry.pastOther}`;
};

const RELATIVE = {
  lastWeek: "eeee 'iliyopita, saa' p",
  yesterday: "'jana saa' p",
  today: "'leo saa' p",
  tomorrow: "'kesho saa' p",
  nextWeek: "eeee 'ijayo, saa' p",
  other: "P",
};

const sw = {
  ...enGB,
  code: "sw",
  formatDistance,
  formatLong,
  formatRelative: (token) => RELATIVE[token],
  localize,
};

export default sw;
